import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../config/app-settings'
import { useGetCampaignsQuery } from '../../redux/apis/campaignsManagement/campaigns/cMCampaignsApiSlice'
import Loader from '../pages/loader'
import { Card, CardBody } from '../../components/other/card/card'
import CampaignCard from '../../components/other/card/campaignCard'

const CampaignsAnalytics = React.memo(() => {
  const context = useContext(AppSettings)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 48
  })
  const [searchItem, setSearchItem] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [active, setActive] = useState('all')

  const { data, isLoading, isError, isFetching } = useGetCampaignsQuery({
    page: pagination.current,
    page_size: pagination.pageSize,
    search: searchQuery || undefined,
    status: active === 'all' ? undefined : active === 'active' ? 'Active' : 'Inactive'
  })

  const campaigns = data?.data?.results ?? []
  const totalCount = data?.data?.count ?? 0

  const totalPages = Math.ceil(totalCount / pagination.pageSize)
  const hasNextPage = pagination.current < totalPages
  const hasPrevPage = pagination.current > 1

  const handleNextPage = () => {
    if (hasNextPage && !isFetching) {
      setPagination(prev => ({ ...prev, current: prev.current + 1 }))
    }
  }

  const handlePrevPage = () => {
    if (hasPrevPage && !isFetching) {
      setPagination(prev => ({ ...prev, current: prev.current - 1 }))
    }
  }

  const handleSearch = () => {
    setSearchQuery(searchItem)
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleClearSearch = () => {
    setSearchItem('')
    setSearchQuery('')
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleActiveChange = value => {
    setActive(value)
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  return (
    <div>
      <Loader isLoading={isLoading || isFetching} />

      <div className='flex justify-between'>
        <div className='form-group mb-3 flex items-center gap-3'>
          <input
            type='text'
            className='form-control'
            placeholder='Search'
            value={searchItem}
            onChange={e => setSearchItem(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
          <button
            className='btn btn-outline-theme'
            onClick={handleSearch}
            disabled={isFetching}
          >
            Search
          </button>
          {searchQuery && (
            <button
              className='btn btn-outline-secondary'
              onClick={handleClearSearch}
              disabled={isFetching}
            >
              Clear
            </button>
          )}
        </div>
        <div className='mb-3 flex gap-3'>
          <button
            className={`btn ${active === 'all' ? 'btn-outline-theme' : 'btn-outline-secondary'}`}
            onClick={() => handleActiveChange('all')}
          >
            All
          </button>
          <button
            className={`btn ${active === 'active' ? 'btn-outline-theme' : 'btn-outline-secondary'}`}
            onClick={() => handleActiveChange('active')}
          >
            Active
          </button>
          <button
            className={`btn ${active === 'inactive' ? 'btn-outline-theme' : 'btn-outline-secondary'}`}
            onClick={() => handleActiveChange('inactive')}
          >
            Inactive
          </button>
        </div>
      </div>

      <Card>
        <CardBody className='flex flex-wrap gap-8 justify-center'>
          {campaigns.length === 0 && !isLoading && !isFetching ? (
            <div style={{ textAlign: 'center', margin: '20px', width: '100%' }}>
              <h3>No campaigns found.</h3>
            </div>
          ) : (
            campaigns.map(item => (
              <CampaignCard type={'active'} item={item} key={item?.id} />
            ))
          )}
        </CardBody>
      </Card>

      {!isError && totalCount > 0 && (
        <div className='flex justify-center items-center gap-4 mt-6 mb-4'>
          <button
            className='btn btn-outline-theme'
            onClick={handlePrevPage}
            disabled={!hasPrevPage || isFetching}
            style={{ opacity: !hasPrevPage || isFetching ? 0.5 : 1 }}
          >
            Previous
          </button>

          <div className='flex items-center gap-2'>
            <span className='text-inverse'>
              Page {pagination.current} of {totalPages}
            </span>
            <span className='text-inverse text-opacity-50'>
              ({totalCount} total campaigns)
            </span>
          </div>

          <button
            className='btn btn-outline-theme'
            onClick={handleNextPage}
            disabled={!hasNextPage || isFetching}
            style={{ opacity: !hasNextPage || isFetching ? 0.5 : 1 }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
})

export default CampaignsAnalytics
